/**
 * Employee Web — `/messages` route (CC-76 / be-070).
 *
 * Two-pane chat surface — conversations rail + thread pane. Every
 * authenticated user can message; no permission gate beyond
 * `requireAuth` (already enforced by the parent `_auth` layout).
 *
 * `?peer={peerUserId}` deep-links straight to a conversation — a MESSAGE_THREAD
 * notification routes here so the bell/push lands on the actual thread (the
 * screen has no `/messages/{id}` path; the peer is a search param).
 * `?group={groupId}` does the same for a chat group (GROUP_MESSAGE_RECEIVED).
 */
import type { JSX } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { MessagesScreen } from "@linkhr/employee-self-service";

export const Route = createFileRoute("/_auth/messages")({
  staticData: { breadcrumb: { labelKey: "messages:title" } },
  validateSearch: (search: Record<string, unknown>): { peer?: string; group?: string } => {
    const pick = (v: unknown): string | undefined =>
      typeof v === "string" && v.length > 0 ? v : undefined;
    const peer = pick(search.peer);
    const group = pick(search.group);
    return { ...(peer !== undefined ? { peer } : {}), ...(group !== undefined ? { group } : {}) };
  },
  component: MessagesRoute,
});

function MessagesRoute(): JSX.Element {
  const { peer, group } = Route.useSearch();
  // exactOptionalPropertyTypes: only pass the props that have a value.
  return (
    <MessagesScreen
      {...(peer !== undefined ? { initialPeerId: peer } : {})}
      {...(group !== undefined ? { initialGroupId: group } : {})}
    />
  );
}
